import createVanilla from 'zustand/vanilla';
import create from 'zustand';
import * as hotjarTracking from '@hotjar/webapp/tracking/tools/hotjar';
import { useEffect } from 'react';

type ScheduledTriggerStore = {
  triggers: string[];
  run: (triggerName: string) => void;
  schedule: (triggerName: string) => void;
};

export const scheduledTriggersStore = createVanilla<ScheduledTriggerStore>((set) => ({
  triggers: [],
  run: (trigger: string) =>
    set((state) => {
      const index = state.triggers.findIndex((t) => t === trigger);

      // if the trigger has not been scheduled, do not run it
      if (index === -1) {
        return state;
      }

      hotjarTracking.trigger(trigger);

      // spreading the old state here shouldn't be necessary but it seems there
      // is a zustand-vanilla bug that makes this fail type-check if we don't.
      return { ...state, triggers: state.triggers.slice(index, 1) };
    }),
  schedule: (trigger: string) =>
    set((state) => {
      const index = state.triggers.findIndex((t) => t === trigger);

      // if the trigger is already scheduled, don't add it again
      if (index !== -1) {
        return state;
      }

      return { ...state, triggers: [...state.triggers, trigger] };
    }),
}));

export const useScheduledTriggersStore = create(scheduledTriggersStore);

export const scheduleTrigger = (trigger: string): void => {
  const { schedule } = scheduledTriggersStore.getState();
  schedule(trigger);
};

/**
 * Executes a given trigger on first render if it has been previously scheduled.
 *
 * Once executed, the trigger is removed from the schedule.
 *
 * @param {string} trigger - The trigger to be run.
 * */
export const useScheduledTrigger = (trigger: string): void => {
  const { run } = useScheduledTriggersStore();

  useEffect(() => {
    run(trigger);
  }, [run, trigger]);
};
